<template>
  <div
    class="d-flex flex-column justify-content-center align-content-center min-w-0 mw-100"
  >
    <Label :html-for="labelHtmlFor">{{ searchUserPlaceholder }}</Label>
    <div class="visitor-check-in-field-width">
      <div v-if="isStudentPickUpOrDropOffReasonSelected">
        <VisitorParentSearch
          :input-id="visitorParentSearchInputId"
          :placeholder="searchUserPlaceholder"
          :options="filter.parentOptions"
          :model-value="filter.value"
          @update:model-value="onValueChange({ value: $event })"
          @search="onParentSearch"
        >
          <template #option="{ value: { value } }">
            <VisitorParentSearchItem :data="value" />
          </template>
        </VisitorParentSearch>
      </div>
      <div
        v-else
        ref="visitorVSelectRoot"
        class="v3-custom-select ps__child--consume"
      >
        <VSelect
          :placeholder="searchUserPlaceholder"
          :options="filter.visitorOptions"
          :filterable="false"
          append-to-body
          label="label"
          :value="filter.value"
          @update:model-value="onValueChange"
          @search="onVisitorSearch"
        />
      </div>
    </div>
  </div>
</template>

<script>
import VisitorParentSearchItem from "@/v3components/Forms/Visitor/CheckIns/VisitorParentSearchItem.vue"
import VisitorParentSearch from "@/v3components/Forms/Visitor/CheckIns/VisitorParentSearch.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import moment from "moment-timezone"
import { useStore } from "vuex"
import { reactive, ref, watch, onMounted, nextTick, computed } from "vue"
import { useI18n } from "vue-i18n"
import { debounce } from "lodash"
import { announce } from "@/composables/useLiveAnnouncer"

export default {
  name: "VisitorSearchField",
  components: {
    Label,
    VisitorParentSearch,
    VisitorParentSearchItem
  },
  props: {
    searchUserPlaceholder: {
      type: String,
      default: ""
    },
    isSisSettingEnabled: {
      type: Boolean,
      default: false
    },
    isStudentPickUpOrDropOffReasonSelected: {
      type: Boolean,
      default: false
    }
  },
  emits: ["onError", "onValueChange"],
  setup(props, { emit }) {
    const store = useStore()
    const { t } = useI18n()
    const visitorVSelectRoot = ref(null)
    const visitorParentSearchInputId = "visitor-checkin-parent-search-combobox"
    const uid = Math.random().toString(36).slice(2, 11)
    const visitorVSelectSearchInputId = `visitor-checkin-visitor-search-${uid}`

    const labelHtmlFor = computed(() =>
      props.isStudentPickUpOrDropOffReasonSelected
        ? visitorParentSearchInputId
        : visitorVSelectSearchInputId
    )

    const filter = reactive({
      search: "",
      value: null,
      parentOptions: [],
      visitorOptions: [],
      lastVisitorSearch: ""
    })

    const syncVueSelectAccessibleName = () => {
      if (props.isStudentPickUpOrDropOffReasonSelected) return
      nextTick(() => {
        const root = visitorVSelectRoot.value
        if (!root) return
        const label =
          props.searchUserPlaceholder?.trim() || "Search visitor name"
        const searchInput = root.querySelector(".vs__search")
        const combo = root.querySelector('[role="combobox"]')
        if (searchInput) {
          searchInput.setAttribute("id", visitorVSelectSearchInputId)
          searchInput.setAttribute("aria-label", label)
          searchInput.removeAttribute("aria-labelledby")
        }
        if (combo) {
          combo.setAttribute("aria-label", label)
        }
      })
    }

    watch(
      () => [
        props.searchUserPlaceholder,
        props.isStudentPickUpOrDropOffReasonSelected
      ],
      () => syncVueSelectAccessibleName(),
      { flush: "post" }
    )

    watch(
      () => [filter.visitorOptions.length, filter.lastVisitorSearch],
      () => syncVueSelectAccessibleName(),
      { flush: "post" }
    )

    const announceEmptySearchResults = (fieldLabel) => {
      const msg = t("common.sorry_no_matching_options")
      const label = fieldLabel?.trim?.() || ""
      announce(label ? `${label}. ${msg}` : msg, { assertive: true })
    }

    watch(
      () => [filter.visitorOptions.length, filter.lastVisitorSearch],
      ([optionCount, query]) => {
        if (props.isStudentPickUpOrDropOffReasonSelected) return
        if (!query?.trim?.() || optionCount > 0) return
        announceEmptySearchResults(props.searchUserPlaceholder)
      }
    )

    onMounted(() => syncVueSelectAccessibleName())

    const onParentSearch = debounce(async (search) => {
      if (search.trim().length === 0) {
        filter.parentOptions = []
        return
      }
      try {
        const {
          data: { data = [] }
        } = await store.dispatch("visitorManage/searchParent", {
          search
        })

        if (data?.length === 0) {
          filter.parentOptions = []
          return
        }

        filter.parentOptions =
          data?.map((parent) => {
            return {
              label: `${parent?.parent_first_name} ${parent?.parent_last_name}`,
              value: {
                ...parent,
                date_of_birth: parent?.date_of_birth
                  ? moment(parent?.date_of_birth, "MM/DD/YYYY").format(
                      "YYYY-MM-DD"
                    )
                  : ""
              }
            }
          }) || []
      } catch (error) {
        emit("onError", error)
      }
    }, 500)

    const onVisitorSearch = debounce(async (search) => {
      if (search.trim().length === 0) {
        filter.visitorOptions = []
        filter.lastVisitorSearch = ""
        return
      }
      try {
        const {
          data: { data = [] }
        } = await store.dispatch("visitorManage/searchVisitor", {
          search
        })

        if (data?.length === 0) {
          filter.visitorOptions = []
          filter.lastVisitorSearch = search
          return
        }

        filter.lastVisitorSearch = search
        filter.visitorOptions =
          data?.map((visitor) => {
            return {
              label: `${visitor?.first_name} ${visitor?.last_name}`,
              value: visitor
            }
          }) || []
      } catch (error) {
        emit("onError", error)
      }
    }, 500)

    const onValueChange = (value) => {
      filter.value = value
      emit("onValueChange", value)
    }

    return {
      visitorVSelectRoot,
      visitorParentSearchInputId,
      labelHtmlFor,
      onParentSearch,
      filter,
      onVisitorSearch,
      onValueChange
    }
  }
}
</script>

<style scoped>
.w-350px {
  width: 350px;
}
</style>
